import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { GameScene } from '@Core/GameScene/GameScene';
import { CycleStep } from '@Core/SymbolsHighlighter/SymbolsHighlighter.types';
import { ESymbolsHighlighterPhases } from '@Core/SymbolsHighlighter/SymbolsHighlighterPhases';
import { TextContainerConfig } from '@Core/TextContainer/TextContainer.types';
import { setContainerScaleToFitSpineSize } from '@Core/Utils/ContainerUtils';
import { LayerReparentHelper } from '@Core/Utils/LayerReparentHelper';
import { getSymbolJackpotLevels, getSymbolRoles } from '@Core/Utils/SymbolUtils';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { EShortAnimationMode, IMultiSymbolFeature } from '@GFTypes/GameStoreTypes';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import { TPoint } from '@GFTypes/Point';
import { TSymbolSpineAsset } from '@GFTypes/SymbolAsset';
import EventsCenter, { IEventData } from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { Container, DestroyOptions, Point } from 'pixi.js';
import { SymbolSpinePool } from '../SpinePool/SymbolSpinePool';
import Reel from './Reel';
import Symbol from './Symbol';
import { ESymbolAnimations } from './SymbolAnimations';
import { ESymbolRoles } from './SymbolRoles';

type TReelSymbolLayers = ELayeringOrder.LOSING_SYMBOLS_LAYER | ELayeringOrder.WINNING_SYMBOLS_LAYER | ELayeringOrder.WINNING_SYMBOLS_NOT_ON_LINE_LAYER;

export default class ReelSymbol extends Symbol {
    public static readonly BLOCKER_NAME = 'blockWhenSymbolIsBlocked';
    public static readonly ODOMETER_PLACEHOLDER_SLOT_NAME = 'bonus_odometer_placeholder';
    public static readonly MAX_Z_INDEX: 99999;
    public simulationBodyId: number;
    private placeholderContainer: Container | null;
    private isWinning: boolean = false;
    private readonly layers: Record<TReelSymbolLayers, Container> = {
        [ELayeringOrder.LOSING_SYMBOLS_LAYER]: this.scene.reels.losingSymbolsLayer,
        [ELayeringOrder.WINNING_SYMBOLS_LAYER]: this.scene.reels.winningSymbolsLayer,
        [ELayeringOrder.WINNING_SYMBOLS_NOT_ON_LINE_LAYER]: this.scene.reels.winningSymbolsNotOnLineLayer
    };

    constructor(
        reel: Reel,
        scene: GameScene,
        position: TPoint,
        symbolSpineConfig: TSymbolSpineAsset,
        symbolId: number,
        reelIndex: number,
        symbolIndex: number,
        multiSymbols?: IMultiSymbolFeature
    ) {
        super(reel, scene, position, symbolSpineConfig, symbolId, reelIndex, symbolIndex, multiSymbols);
        this.simulationBodyId = symbolIndex;
        EventsCenter.on(EGameStates.PRE_SPIN_STATE, this.moveSymbolToReel, this);
        EventsCenter.on(EGameStates.PRE_WIN_STATE, this.onReelAnimationEnd, this);
        EventsCenter.on(EGameEvents.REEL_SPIN_END_$, this.handleReelEnded, this);
        EventsCenter.on(EGameEvents.CYCLE_WINLINE, this.startWinAnimation, this);
        EventsCenter.on(EGameEvents.CYCLE_WAYS, this.startWinAnimation, this);
        EventsCenter.on(EGameEvents.CYCLE_SCATTERS, this.startWinAnimation, this);
        EventsCenter.on(EGameEvents.RESIZE_SYMBOLS, this.onSymbolsResize, this);
        EventsCenter.on(EGameEvents.TAKE, this.stopAnimation, this);
        EventsCenter.on(EGameEvents.STOP_SYMBOL_ANIMATIONS, this.stopAnimationOnStep, this);
        EventsCenter.on(EGameStates.PRE_GAMBLE_STATE, this.stopAnimation, this);
    }

    private get numberOfSymbols(): number {
        return this.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
    }

    private get winningPositions() {
        return this.gameStore.get(EGameStoreKeys.BaseWinPresentationState).winningPositions;
    }

    private isVisibleSymbol(): boolean {
        return this.symbolIndex > 0 && this.symbolIndex <= this.numberOfSymbols;
    }

    private moveSymbolToReel(): void {
        if (this.reel.children.includes(this)) return;
        LayerReparentHelper.restoreToContainer(this, this.reel, this.reelIndex, this.placeholderContainer);

        this.placeholderContainer = null;
        this.isWinning = false;
    }

    private moveSymbolAboveReels(): void {
        const isInLosingLayer = this.parent === this.scene.reels.losingSymbolsLayer;
        const isWinningNotOnLineLayer = this.parent === this.scene.reels.winningSymbolsNotOnLineLayer;
        const isInWinningLayer = this.parent === this.scene.reels.winningSymbolsLayer;
        if (isInLosingLayer || isInWinningLayer || isWinningNotOnLineLayer) return;

        const isWinning = this.winningPositions?.[this.reelIndex][this.visibleSymbolIndex];
        const layer = isWinning ? this.scene.reels.winningSymbolsLayer : this.scene.reels.losingSymbolsLayer;
        this.placeholderContainer = LayerReparentHelper.moveToLayer(this, this.reel, layer, this.scene.stage);

        this.sortLayers();
    }

    private onReelAnimationEnd(): void {
        const win = this.gameStore.get(EGameStoreKeys.SpinOutcome).win > 0;
        if (!win || !this.isVisibleSymbol()) return;
        this.moveSymbolAboveReels();
    }

    private handleReelEnded(data: IEventData): void {
        this.onReelSpinEnd(data.eventIds![0]);
    }

    private onReelSpinEnd(reelIndex: number): void {
        const potentialWinningSymbols = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0].potentialWinningSymbols;
        const isLockAndRollMode = this.gameStore.get(EGameStoreKeys.IsLockAndRollMode);
        this.isBlurred = false;
        const ids = this.isMultiSymbol ? this.componentSymbolIds : [this.symbolId];
        const shortAnimationModes = ids.map(
            (id) => this.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((config) => config.id === id)?.shortAnimationMode
        );
        const shouldAlwaysPlayShortAnimation = shortAnimationModes?.includes(EShortAnimationMode.ALWAYS);

        if (this.reelIndex !== reelIndex - 1 || !this.isVisibleSymbol()) return;
        if (!this.symbolAnimation?.eventManager?.hasGameEventAnimation(ESymbolAnimations.SHORT)) return;
        if (!shouldAlwaysPlayShortAnimation && potentialWinningSymbols && !potentialWinningSymbols[this.reelIndex].toReversed()[this.visibleSymbolIndex])
            return;
        if (this.scene.stickySymbols.stickySymbols?.[this.reelIndex]?.[this.visibleSymbolIndex]) return;
        if (isLockAndRollMode && !getSymbolRoles(this.gameStore, this.symbolId).includes(ESymbolRoles.LOCK_AND_ROLL_COIN)) return;

        this.moveSymbolAboveReels();
        this.playShortAnimation();
    }

    private startWinAnimation(step: CycleStep): void {
        if (!this.isVisibleSymbol()) return;

        const isInWinningSymbols = this.winningPositions?.[this.reelIndex][this.visibleSymbolIndex];
        const shouldHighlightAllWinningSymbols = this.gameStore.get(EGameStoreKeys.WinConfiguration).highlightAllWinningSymbols;
        const isInSelectionMask = step.selectionMask[this.reelIndex]?.[this.visibleSymbolIndex];
        const { powerLines, payLines } = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig);
        const isWinlineWin = !powerLines && payLines.length > 0;

        if (step.phase === ESymbolsHighlighterPhases.DYNAMIC_CYCLE_PHASE && isWinlineWin) {
            this.updateSymbolLayerForCycleStep(step, isWinlineWin);
            this.startDynamicCycleWinAnimation(step, isInWinningSymbols);
        } else {
            this.updateSymbolLayerForCycleStep(step, isWinlineWin);
            this.startNormalCycleWinAnimation(step, isInWinningSymbols, shouldHighlightAllWinningSymbols, isInSelectionMask);
        }
    }

    private startNormalCycleWinAnimation(
        step: CycleStep,
        isInWinningSymbols: boolean,
        shouldHighlightAllWinningSymbols: boolean,
        isInSelectionMask: boolean
    ): void {
        if (isInSelectionMask) {
            this.playWinAnimation(step);
        } else if (isInWinningSymbols && shouldHighlightAllWinningSymbols) {
            this.playWinNotOnLineAnimation(step);
        } else {
            this.playNotWinAnimation();
        }
    }

    private startDynamicCycleWinAnimation(step: CycleStep, isInWinningSymbols: boolean): void {
        if (isInWinningSymbols) {
            this.playWinAnimation(step, false);
        } else {
            this.playNotWinAnimation();
        }
    }

    private playWinAnimation(step: CycleStep, reset: boolean = true): void {
        const winAnimationVariant = this.getAnimationVariant(ESymbolAnimations.WIN, step);
        const actualAnimation = this.symbolAnimation.eventManager.getAnimationNameForGameEvent(winAnimationVariant);
        const isCurrentlyPlaying = this.symbolAnimation.getCurrentAnimationName(0) === actualAnimation;

        if (isCurrentlyPlaying && !reset) return;

        this.symbolAnimation.eventManager.playEventAnimation(winAnimationVariant, undefined, undefined, undefined, undefined, true, true);
        this.isWinning = true;
    }

    private playWinNotOnLineAnimation(step: CycleStep): void {
        const winNotOnWinlineAnimationVariant = this.getAnimationVariant(ESymbolAnimations.WIN_NOT_ON_LINE, step);
        if (this.symbolAnimation.eventManager.hasGameEventAnimation(ESymbolAnimations.WIN_NOT_ON_LINE)) {
            this.symbolAnimation.eventManager.playEventAnimation(winNotOnWinlineAnimationVariant, undefined, undefined, undefined, undefined, true, true);
            this.isWinning = true;
        } else {
            this.playOnlyFirstFrameOfWinAnimation(step);
        }
    }

    private playOnlyFirstFrameOfWinAnimation(step: CycleStep): void {
        this.playWinAnimation(step);
        this.symbolAnimation.spine.state.update(0);
        const currentTrack = this.symbolAnimation.getCurrentAnimationTrack();
        if (currentTrack) {
            currentTrack.timeScale = 0;
            currentTrack.trackTime = 0;
        }
    }
    private playNotWinAnimation(): void {
        this.symbolAnimation.eventManager.playEventAnimation(ESymbolAnimations.NOTWIN, undefined, undefined, undefined, undefined, true, true);
        this.isWinning = false;
    }

    private sortLayers(): void {
        Object.values(this.layers).forEach((layer) => {
            layer.children.forEach((el: any) => {
                el.zIndex = el.isWinning ? ReelSymbol.MAX_Z_INDEX : el.y;
            });

            layer.sortChildren();
        });
    }

    private onSymbolsResize(): void {
        const reelParams = this.scene.game.gameStore.get(EGameStoreKeys.ReelParameters);
        const { baseWidth, baseHeight, width, height } = this.scene.game.gameStore.get(EGameStoreKeys.SymbolParameters);

        const isInLosingLayer = this.parent === this.scene.reels.losingSymbolsLayer;
        const isInWinningLayer = this.parent === this.scene.reels.winningSymbolsLayer;
        const isInWinningNotOnLineLayer = this.parent === this.scene.reels.winningSymbolsNotOnLineLayer;
        if (isInWinningLayer || isInLosingLayer || isInWinningNotOnLineLayer) {
            const layer = isInWinningLayer ? this.scene.reels.winningSymbolsLayer : this.scene.reels.losingSymbolsLayer;

            if (this.placeholderContainer) {
                const newX = this.placeholderContainer.x * (reelParams.width / reelParams.prevWidth);
                const newY = this.placeholderContainer.y * (reelParams.height / reelParams.prevHeight);

                this.placeholderContainer.position = new Point(newX, newY);

                const localPoint = new Point(this.placeholderContainer.x, this.placeholderContainer.y);
                const globalPoint = this.reel.toGlobal(localPoint);
                const point = layer.toLocal(globalPoint);
                this.position = new Point(point.x, point.y);
                setContainerScaleToFitSpineSize(this, this.symbolAnimation, { baseWidth, baseHeight, width, height });
            }
        } else {
            const newX = this.x * (reelParams.width / reelParams.prevWidth);
            const newY = this.y * (reelParams.height / reelParams.prevHeight);
            this.position = new Point(newX, newY);
            setContainerScaleToFitSpineSize(this, this.symbolAnimation, { baseWidth, baseHeight, width, height });
        }
    }

    public setLockAndRollCoinValue(real: boolean | undefined): void {
        let slotsToUpdate: number[] = this.componentSymbolIds;

        if (this.componentSymbolIds.length === 0) {
            const symbolRoles = getSymbolRoles(this.gameStore, this.symbolId);
            slotsToUpdate = symbolRoles.includes(ESymbolRoles.LOCK_AND_ROLL_COIN) ? [this.symbolId] : [];
        }

        if (!slotsToUpdate.length || !this.scene.lockAndRollCoinValues) return;

        const coinLabelConfig = ConfigLoader.getConfiguration(EConfigurationNames.TEXTS).styles?.['lock_and_roll_coin_label'] as TextContainerConfig;
        const slotNamePrefix = this.componentSymbolIds.length > 0 ? this.multiSymbols!.config.labelSlotNamePrefix : undefined;

        slotsToUpdate.forEach((symbolId, index) => {
            if (symbolId === 0) return;

            const slotName = slotNamePrefix ? `${slotNamePrefix}${index + 1}` : ReelSymbol.ODOMETER_PLACEHOLDER_SLOT_NAME;

            if (!this.symbolAnimation.spine.skeleton.findSlot(slotName)) return;

            const hasJackpot = getSymbolJackpotLevels(this.gameStore, symbolId).length > 0;
            const reelIndex = this.isMultiSymbol ? this.reelIndex * 2 : this.reelIndex;
            const realCoinValue = this.scene.lockAndRollCoinValues.getRealCoinValue(reelIndex + index, this.symbolIndex);
            const randomCoinValue = this.scene.lockAndRollCoinValues.getRandomCoinValue();
            const coinValue = real ? realCoinValue || randomCoinValue : randomCoinValue;

            const value = hasJackpot ? undefined : coinValue;

            if (value) this.setSymbolLabel(value, slotName, coinLabelConfig);
        });
    }

    public changeSymbol(
        symbolId: number,
        symbolSpineConfig: TSymbolSpineAsset,
        reelIndex: number,
        symbolIndex: number,
        pool: SymbolSpinePool,
        real?: boolean
    ): void {
        super.changeSymbol(symbolId, symbolSpineConfig, reelIndex, symbolIndex, pool);
        this.setLockAndRollCoinValue(real);
        this.darkenNormalSymbolInLockAndRoll();
    }

    public stopAnimation(): void {
        this.moveSymbolToReel();
        super.stopAnimation();
    }

    public stopAnimationOnStep(): void {
        this.moveSymbolToReel();
        if (this.symbolAnimation.getCurrentAnimationName() === ESymbolAnimations.SHORT) return;
        super.stopAnimation();
    }

    private getLayerForCycleStep(step?: CycleStep): TReelSymbolLayers {
        const isWinning = this.winningPositions?.[this.reelIndex][this.visibleSymbolIndex];
        const isOnWinningLine = step?.selectionMask[this.reelIndex]?.[this.visibleSymbolIndex];
        if (!isWinning) return ELayeringOrder.LOSING_SYMBOLS_LAYER;
        if (isOnWinningLine) return ELayeringOrder.WINNING_SYMBOLS_LAYER;
        return ELayeringOrder.WINNING_SYMBOLS_NOT_ON_LINE_LAYER;
    }

    private getLayerDynamicWinlineStep(step: CycleStep): TReelSymbolLayers {
        const isWinning = this.winningPositions?.[this.reelIndex][this.visibleSymbolIndex];
        if (isWinning) return ELayeringOrder.WINNING_SYMBOLS_LAYER;
        return ELayeringOrder.LOSING_SYMBOLS_LAYER;
    }

    private updateSymbolLayerForCycleStep(step: CycleStep, isWinlineWin: boolean): void {
        const targetLayer =
            isWinlineWin && step?.phase === ESymbolsHighlighterPhases.DYNAMIC_CYCLE_PHASE
                ? this.getLayerDynamicWinlineStep(step)
                : this.getLayerForCycleStep(step);

        const targetContainer = this.layers[targetLayer];
        if (targetContainer.children.includes(this)) return;

        if (this.placeholderContainer) {
            LayerReparentHelper.restoreToContainer(this, this.reel, this.reelIndex, this.placeholderContainer);
            this.placeholderContainer = null;
        }

        this.placeholderContainer = LayerReparentHelper.moveToLayer(this, this.reel, targetContainer, this.scene.stage);
    }

    public darkenNormalSymbolInLockAndRoll(): void {
        const isLockAndRoll = this.gameStore.get(EGameStoreKeys.IsLockAndRollMode);
        if (isLockAndRoll && !getSymbolRoles(this.gameStore, this.symbolId).includes(ESymbolRoles.LOCK_AND_ROLL_COIN)) {
            this.playNotWinAnimation();
        }
    }

    public switchToSpinningAnimation(): void {
        this.applyBlur();
        this.darkenNormalSymbolInLockAndRoll();
    }

    public destroy(options?: DestroyOptions): void {
        EventsCenter.off(EGameStates.PRE_SPIN_STATE, this.moveSymbolToReel, this);
        EventsCenter.off(EGameStates.PRE_WIN_STATE, this.onReelAnimationEnd, this);
        EventsCenter.off(EGameEvents.REEL_SPIN_END_$, this.handleReelEnded, this);
        EventsCenter.off(EGameEvents.CYCLE_WINLINE, this.startWinAnimation, this);
        EventsCenter.off(EGameEvents.CYCLE_WAYS, this.startWinAnimation, this);
        EventsCenter.off(EGameEvents.CYCLE_SCATTERS, this.startWinAnimation, this);
        EventsCenter.off(EGameEvents.RESIZE_SYMBOLS, this.onSymbolsResize, this);
        EventsCenter.off(EGameEvents.TAKE, this.stopAnimation, this);
        EventsCenter.off(EGameEvents.STOP_SYMBOL_ANIMATIONS, this.stopAnimationOnStep, this);
        EventsCenter.off(EGameStates.PRE_GAMBLE_STATE, this.stopAnimation, this);
        super.destroy(options);
    }
}
